import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Bridges third-party "add to cart" actions to the theme's cart UI.
 *
 * The Easy Bundles / Quantity Breaks (CartMax) bundle widget posts straight to
 * the Shopify cart endpoint without dispatching the theme's `cart:update`
 * event, so the header cart bubble and cart drawer don't refresh until a full
 * page reload. We observe cart writes (fetch + XHR), then reconcile the theme
 * UI against the authoritative cart via the theme's own CartUpdateEvent.
 *
 * The reconcile SETS the absolute item count (source is intentionally not
 * 'product-form-component', which is what the header bubble treats as an
 * increment). That makes it idempotent and safe even when it also fires
 * alongside a native theme add — the final state is always the true cart total.
 */

// Matches /cart/add, /cart/change, /cart/update, /cart/clear (optionally .js).
const CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/i;

let reconcileTimer = null;
/** Time of the most recently observed cart write. */
let pendingSince = 0;
/** Time of the most recent NATIVE theme cart update (i.e. the theme handled it itself). */
let lastNativeUpdate = 0;

// When the theme's own add-to-cart runs it dispatches `cart:update` and refreshes
// the UI itself. Track those so the bridge can stand down and avoid a redundant
// second render — it only needs to act for adds the theme didn't handle (the app's).
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const source = /** @type {any} */ (event).detail?.data?.source;
  if (source !== 'bundle-cart-sync') lastNativeUpdate = Date.now();
});

function scheduleReconcile() {
  // Debounce — a single bundle can add several lines in quick succession.
  pendingSince = Date.now();
  clearTimeout(reconcileTimer);
  reconcileTimer = setTimeout(reconcileCart, 150);
}

async function reconcileCart() {
  // The theme already synced the UI for this write — nothing to do.
  if (lastNativeUpdate >= pendingSince) return;

  try {
    const cartUrl = `${window.Theme?.routes?.cart_url || '/cart'}.js`;
    const cart = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((r) => r.json());

    // Cart sections currently on the page (drawer and/or the cart page itself).
    const sectionIds = [
      ...new Set(
        [...document.querySelectorAll('cart-items-component[data-section-id]')]
          .map((el) => el.dataset.sectionId)
          .filter(Boolean)
      ),
    ];

    let sections = {};
    if (sectionIds.length) {
      const url = `${window.location.pathname}?sections=${sectionIds.join(',')}`;
      sections = await fetch(url, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .catch(() => ({}));
    }

    document.dispatchEvent(
      new CartUpdateEvent(cart, 'bundle-cart-sync', {
        source: 'bundle-cart-sync',
        itemCount: cart.item_count,
        sections,
      })
    );
  } catch (error) {
    console.warn('[bundle-cart-sync] cart reconcile failed', error);
  }
}

/**
 * @param {string} method
 * @param {string} url
 * @returns {boolean}
 */
function isCartWrite(method, url) {
  return method.toUpperCase() === 'POST' && CART_WRITE.test(url);
}

// --- fetch ---
const nativeFetch = window.fetch;
if (typeof nativeFetch === 'function') {
  window.fetch = function (...args) {
    const result = nativeFetch.apply(this, args);
    try {
      const input = args[0];
      const init = args[1] || {};
      const url = input instanceof Request ? input.url : String(input);
      const method = init.method || (input instanceof Request ? input.method : 'GET') || 'GET';
      if (isCartWrite(method, url)) {
        result.then((response) => response?.ok && scheduleReconcile()).catch(() => {});
      }
    } catch {
      // never let the bridge break the underlying request
    }
    return result;
  };
}

// --- XMLHttpRequest (some apps still use it) ---
const nativeOpen = XMLHttpRequest.prototype.open;
const nativeSend = XMLHttpRequest.prototype.send;

XMLHttpRequest.prototype.open = function (method, url, ...rest) {
  try {
    this.__isCartWrite = isCartWrite(String(method), String(url));
  } catch {
    this.__isCartWrite = false;
  }
  return nativeOpen.call(this, method, url, ...rest);
};

XMLHttpRequest.prototype.send = function (...args) {
  if (this.__isCartWrite) {
    this.addEventListener('load', () => {
      if (this.status >= 200 && this.status < 300) scheduleReconcile();
    });
  }
  return nativeSend.apply(this, args);
};
